import { createContext, useContext } from 'react'
import type { ApiFailure, Schema } from '@/api/client'

export type Tenant = Schema<'TenantOut'>
export type Membership = Schema<'MembershipOut'>
export type TenantMembership = Schema<'TenantMembershipOut'>
export type Role = Schema<'Role'>

export interface TenantValue {
  status: 'loading' | 'ready' | 'error'
  failure: ApiFailure | null
  /** Every organisation this user belongs to — what fills the switcher. */
  memberships: TenantMembership[]
  /** The one the *session* is in. Null until an organisation is selected. */
  active: TenantMembership | null
  /** Switch the session's organisation. Resolves once the switch has landed. */
  select: (tenantId: string) => Promise<boolean>
  /**
   * Add an organisation the app has just been handed, without waiting for a
   * refetch. Creating one and accepting an invitation both return the tenant
   * *and* select it on the session, so at that moment both halves are known —
   * and navigating into the shell before a `reload()` lands would bounce off
   * `RequireTenant` and back to the picker.
   */
  adopt: (row: TenantMembership) => void
  reload: () => void
}

export const TenantContext = createContext<TenantValue | null>(null)

export function useTenants(): TenantValue {
  const value = useContext(TenantContext)
  if (!value) throw new Error('useTenants was called outside <TenantProvider>')
  return value
}

/**
 * The active organisation, for screens the tenant guard has already vetted.
 *
 * Throws rather than returning null for the same reason as `useCurrentUser`:
 * the router has made the null case unreachable, and a screen that renders with
 * an empty organisation name is a wiring bug that should be loud.
 */
export function useActiveTenant(): { tenant: Tenant; membership: Membership; role: Role } {
  const { active } = useTenants()
  if (!active) throw new Error('useActiveTenant was called outside a tenant-scoped route')
  return { tenant: active.tenant, membership: active.membership, role: active.membership.role }
}

/**
 * The permission matrix, mirrored from `api/app/tenancy/roles.py`.
 *
 * A mirror, not a source of truth — the API refuses the request regardless, and
 * this exists only so the UI does not offer a button that is going to 403. It
 * is deliberately *not* generated: OpenAPI describes shapes, not authorisation,
 * so there is nothing to generate it from. The rule that keeps it honest is
 * that every entry corresponds to a `Permission` on the server, and the
 * server's refusal is what actually enforces it.
 */
export const ROLE_CAPABILITIES: Record<Role, ReadonlySet<string>> = {
  owner: new Set([
    'tenant:read',
    'tenant:update',
    'members:read',
    'members:invite',
    'members:remove',
    'members:role',
    'notifications:manage',
    // S-B12. `tenant:delete` is owner-only on the server too, and for the
    // reason stated there: deleting an organisation destroys every other
    // member's work.
    'audit:read',
    'data:export',
    'suppression:manage',
    'tenant:delete',
    // S-B6. `billing:manage` is owner-only on the server too, and S-D3's
    // approved role matrix is what decides it: *Billing & cancellation* has
    // a tick in the Owner column and a dash in every other one.
    'billing:read',
    'billing:manage',
  ]),
  admin: new Set([
    'tenant:read',
    'tenant:update',
    'members:read',
    'members:invite',
    'members:remove',
    'notifications:manage',
    'audit:read',
    'data:export',
    'suppression:manage',
    // Reading only. An accountant asks for a GST invoice, and one only the
    // founder can fetch is one that holds up a filing — the same argument
    // S-B12 made for `data:export`.
    'billing:read',
  ]),
  member: new Set(['tenant:read', 'members:read']),
}

export function may(role: Role, permission: string): boolean {
  return ROLE_CAPABILITIES[role].has(permission)
}
